import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService, UrlService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, ProfileSettingViewModel } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProjectService } from "../services/projectService.g";

@Component({
    selector: "project-summary-chart",
    templateUrl: "./projectSummaryChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class ProjectSummaryChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $intentService = inject(IntentService);
    private readonly $projectService = inject(ProjectService);
    private readonly $chartService = inject(ChartService);

    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;

    private _chartCriteria: IChartCriteria;
    readonly ChartTitle: string = "Monthly Project Spending";

    isLoaded: boolean;
    chartConfig: ChartConfiguration<"bar">;
    readonly showChart = signal(false);

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        if (this.chartCriteria.ChartDatas?.length > 0 && this.chartCriteria.ChartDatas[0].Additional?.length > 0)
            return this.chartCriteria.ChartDatas[0].Additional[0];
        return "";
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getParameterById(parameterId: string): ProfileSettingViewModel | undefined
    {
        const parameters = this.chartCriteria?.Parameters?.find(g => g.Id === parameterId);
        return parameters ? parameters[0] : undefined;
    }

    private hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0
            && !!this.chartCriteria.ChartDatas[0].Data;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;

                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                const newValue = this.getParameterById("ProjectSummaryGroupById");
                if (newValue)
                {
                    this.$projectService.GetProjectSubFilters(newValue.Value).then(result =>
                    {
                        const sub = this.getParameterById("ProjectSummaryGroupBySubId");
                        if (!sub)
                        {
                            return;
                        }
                        sub.Options = [
                            {
                                Value: null,
                                Display: "View All",
                            },
                            ...result.map(x =>
                            {
                                return {
                                    Value: x.Id,
                                    Display: x.Description,
                                };
                            }),
                        ];
                    });
                }

                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors();
        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                let label = tooltipItem.label;
                                if (this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterParam)
                                {
                                    label = `${this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterParam.Value}`;
                                }

                                const count = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Count;
                                const amount = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Amount;
                                return `${label} (${count}): ${formatCurrency(amount, this.locale, "$")}`;
                            },
                        },
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (Utilities.IsNullOrUndefined(element))
                    {
                        return;
                    }
                    const index = element.index;
                    const filter: FilterItem[] = [];
                    if (this.chartCriteria.ChartDatas[0].Additional[1])
                    {
                        for (let i = 0; i < this.chartCriteria.ChartDatas[0].Additional[1].length; i++)
                        {
                            const filterItem = this.chartCriteria.ChartDatas[0].Additional[1][i];
                            filter.push(filterItem);
                        }
                    }
                    if (!Utilities.IsNullOrUndefined(this.chartCriteria.ChartDatas[0].Data[index].FilterParam))
                    {
                        filter.push(this.chartCriteria.ChartDatas[0].Data[index].FilterParam);
                    }
                    const parms: AppLinkParms = {
                        App: "projectaccounting",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms)
                        .then(({ Result: result }: ExecutionResult<string>) =>
                        {
                            const index = result.indexOf("?");
                            let q = "";
                            if (index > 0)
                            {
                                q = result.substring(index, result.length);
                            }
                            this.$intentService.resolve("Projects", "Search", { querystring: q });
                        });
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Amount),
                        backgroundColor: chartColors,
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.hasData());
    }
}
